/**
 * useCRMQueries.ts
 * 
 * Standalone React Query hooks for CRM data (leads, clients, appointments).
 * These hooks can be used independently of CRMContext, enabling lazy fetching
 * from components that only need a specific slice of CRM data.
 * 
 * CRMContext.tsx internally delegates to these hooks for backward compatibility.
 */
import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { requireWorkspaceId } from '../services/database';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useIdioma } from '../i18n/idioma';
import type { Traducir } from '../i18n/idioma';
import {
    findDuplicateContact, describeDuplicateContact, duplicateContactError,
    clientContactRecord, leadContactRecord, isLeadEligibleForDedup,
    normalizePhone, normalizeEmail, normalizeAddress, normalizeName,
    type ContactRecord,
} from '../services/clientValidation';
import {
    isLeadStageLocked, isLeadRemovableFromBoard, stageLockedError, describeStageBlock,
} from '../utils/leadStageFlow';
import type { Lead, Client, Appointment, LeadStatus } from '../types';

// ──────────────────────────────────────────────────────────────
// DB ↔ App Mappers
// ──────────────────────────────────────────────────────────────

/**
 * Text columns are nullable in the `clients` table, but every consumer treats
 * them as strings (`c.address.toLowerCase()`, `v.email.trim()`, …). Coercing
 * here — the single point every client row passes through — keeps one legacy
 * NULL from taking down the whole Clients screen or the edit form.
 */
export const mapClientFromDB = (db: any): Client => ({
    id: db.id,
    name: db.name ?? '',
    phone: db.phone ?? '',
    email: db.email ?? '',
    address: db.address ?? '',
    /** @deprecated Never recomputed — always 0. Lifetime revenue is derived
     *  live from COMPLETED projects. Do not read this for display. */
    totalSpent: db.total_spent ?? 0,
    propertyNotes: db.property_notes ?? '',
    gateCode: db.gate_code ?? '',
    preferredContact: db.preferred_contact ?? 'Phone',
    tags: db.tags ?? [],
    dateCreated: db.date_created,
});

export const mapClientToDB = (client: any, workspaceId: string) => {
    const data: any = { workspace_id: workspaceId };
    if (client.name !== undefined) data.name = client.name;
    if (client.phone !== undefined) data.phone = client.phone;
    if (client.email !== undefined) data.email = client.email;
    if (client.address !== undefined) data.address = client.address;
    if (client.totalSpent !== undefined) data.total_spent = client.totalSpent;
    if (client.propertyNotes !== undefined) data.property_notes = client.propertyNotes;
    if (client.gateCode !== undefined) data.gate_code = client.gateCode;
    if (client.preferredContact !== undefined) data.preferred_contact = client.preferredContact;
    if (client.tags !== undefined) data.tags = client.tags;
    return data;
};

/** Same null-coercion contract as {@link mapClientFromDB} — the pipeline board
 *  filters on `l.address.toLowerCase()` and `l.phone.includes(...)`. */
export const mapLeadFromDB = (db: any): Lead => ({
    id: db.id,
    name: db.name ?? '',
    phone: db.phone ?? '',
    email: db.email ?? '',
    address: db.address ?? '',
    serviceInterest: db.service_interest ?? '',
    status: db.status as LeadStatus,
    notes: db.notes ?? '',
    value: db.value,
    dateCreated: db.date_created,
    nextAction: db.next_action,
    nextActionDate: db.next_action_date,
    tags: db.tags ?? [],
    designProjectId: db.design_project_id,
    wonAt: db.won_at,
});

export const mapLeadToDB = (lead: any, workspaceId: string) => {
    const data: any = { workspace_id: workspaceId };
    if (lead.name !== undefined) data.name = lead.name;
    if (lead.phone !== undefined) data.phone = lead.phone;
    if (lead.email !== undefined) data.email = lead.email;
    if (lead.address !== undefined) data.address = lead.address;
    if (lead.serviceInterest !== undefined) data.service_interest = lead.serviceInterest;
    if (lead.status !== undefined) data.status = lead.status;
    if (lead.notes !== undefined) data.notes = lead.notes;
    if (lead.value !== undefined) data.value = lead.value;
    // Nullable fields: support explicit null to clear DB value.
    if (lead.nextAction === null) data.next_action = null;
    else if (lead.nextAction !== undefined) data.next_action = lead.nextAction;
    // Las fechas: la cadena VACÍA también es «sin fecha». Es lo que devuelve un
    // `<input type="date">` al borrarlo, y las columnas son timestamptz:
    // Postgres la rechazaba («invalid input syntax for type timestamp with
    // time zone: ""», seis veces en producción entre el 7 y el 12-sep-2026) y
    // la caja se quedaba enseñando una fecha que no estaba guardada.
    if (lead.nextActionDate === null || lead.nextActionDate === '') data.next_action_date = null;
    else if (lead.nextActionDate !== undefined) data.next_action_date = lead.nextActionDate;
    if (lead.tags !== undefined) data.tags = lead.tags;
    if (lead.designProjectId !== undefined) data.design_project_id = lead.designProjectId;
    if (lead.wonAt === null || lead.wonAt === '') data.won_at = null;
    else if (lead.wonAt !== undefined) data.won_at = lead.wonAt;
    return data;
};

export const mapAppointmentFromDB = (db: any): Appointment => ({
    id: db.id,
    clientId: db.client_id,
    clientName: db.client_name,
    clientAddress: db.client_address,
    title: db.title,
    date: db.date,
    startTime: db.start_time,
    endTime: db.end_time,
    type: db.type,
    notes: db.notes,
    assignedTo: db.assigned_to,
    durationMinutes: db.duration_minutes,
    // mapAppointmentToDB has always WRITTEN project_id, but this mapper never
    // read it back — so every appointment returned with projectId undefined.
    // Anything keyed off the project link silently saw nothing: the crew
    // schedule dropped every chip (buildScheduleGrid skips !projectId), and the
    // project's own "Scheduled Visits" list was permanently empty. The rows were
    // correct in the database the whole time; they just never came back.
    projectId: db.project_id,
});

export const mapAppointmentToDB = (appt: any, workspaceId: string) => {
    const data: any = { workspace_id: workspaceId };
    if (appt.clientId !== undefined) data.client_id = appt.clientId;
    if (appt.clientName !== undefined) data.client_name = appt.clientName;
    if (appt.clientAddress !== undefined) data.client_address = appt.clientAddress;
    if (appt.title !== undefined) data.title = appt.title;
    if (appt.date !== undefined) data.date = appt.date;
    if (appt.startTime !== undefined) data.start_time = appt.startTime;
    if (appt.endTime !== undefined) data.end_time = appt.endTime;
    if (appt.type !== undefined) data.type = appt.type;
    if (appt.notes !== undefined) data.notes = appt.notes;
    if (appt.assignedTo !== undefined) data.assigned_to = appt.assignedTo;
    if (appt.durationMinutes !== undefined) data.duration_minutes = appt.durationMinutes;
    if (appt.projectId !== undefined) data.project_id = appt.projectId;
    return data;
};


// ──────────────────────────────────────────────────────────────
// Query Keys (exported for cache manipulation in mutations)
// ──────────────────────────────────────────────────────────────

export const CRM_KEYS = {
    leads:        (wsId: string) => ['leads', wsId] as const,
    clients:      (wsId: string) => ['clients', wsId] as const,
    appointments: (wsId: string) => ['appointments', wsId] as const,
};


// ──────────────────────────────────────────────────────────────
// Queries — can be used standalone or from CRMContext
// ──────────────────────────────────────────────────────────────

export function useLeads() {
    const { isAuthorized } = useAuth();
    const { activeWorkspace, status } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useQuery({
        queryKey: CRM_KEYS.leads(wsId),
        queryFn: async () => {
            const { data, error } = await supabase
                .from('leads').select('*')
                .eq('workspace_id', wsId)
                .order('date_created', { ascending: false });
            if (error) throw error;
            return (data || []).map(mapLeadFromDB);
        },
        // Gate on RESOLUTION, not just auth: never fetch against the 'default'
        // fallback (an unresolved/errored workspace would return zero rows and
        // look like the data was deleted).
        enabled: isAuthorized && status === 'resolved' && wsId !== 'default',
    });
}

export function useClients() {
    const { isAuthorized } = useAuth();
    const { activeWorkspace, status } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useQuery({
        queryKey: CRM_KEYS.clients(wsId),
        queryFn: async () => {
            const { data, error } = await supabase
                .from('clients').select('*')
                .eq('workspace_id', wsId)
                .order('date_created', { ascending: false });
            if (error) throw error;
            return (data || []).map(mapClientFromDB);
        },
        // Gate on RESOLUTION, not just auth: never fetch against the 'default'
        // fallback (an unresolved/errored workspace would return zero rows and
        // look like the data was deleted).
        enabled: isAuthorized && status === 'resolved' && wsId !== 'default',
    });
}

export function useAppointments() {
    const { isAuthorized } = useAuth();
    const { activeWorkspace, status } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useQuery({
        queryKey: CRM_KEYS.appointments(wsId),
        queryFn: async () => {
            const { data, error } = await supabase
                .from('appointments').select('*')
                .eq('workspace_id', wsId)
                .order('date', { ascending: true });
            if (error) throw error;
            return (data || []).map(mapAppointmentFromDB);
        },
        // Gate on RESOLUTION, not just auth: never fetch against the 'default'
        // fallback (an unresolved/errored workspace would return zero rows and
        // look like the data was deleted).
        enabled: isAuthorized && status === 'resolved' && wsId !== 'default',
    });
}


// ──────────────────────────────────────────────────────────────
// Mutations
// ──────────────────────────────────────────────────────────────

interface DedupOptions {
    /** La propia ficha al editarla, para que no choque consigo misma. */
    excludeClientId?: string;
    excludeLeadId?: string;
    /** Convertir un prospecto en cliente COPIA su teléfono y su correo, así que
     *  el prospecto de origen bloquearía su propia conversión. Con esto la
     *  agenda de prospectos entera se deja fuera de la comparación: es una
     *  promoción, no un contacto nuevo. */
    skipLeads?: boolean;
    /** Levanta SOLO el bloqueo por nombre repetido. Es para la conversión que
     *  dispara una propuesta FIRMADA desde el enlace público: ahí el trato ya
     *  está cerrado y nadie está delante para reescribir el nombre, así que
     *  bloquear dejaría la propuesta firmada sin ficha de cliente a la que
     *  engancharse. Teléfono, correo y dirección siguen bloqueando. */
    permitirNombreRepetido?: boolean;
    /** Levanta SOLO el bloqueo por dirección repetida. Es para CONVERTIR un
     *  prospecto: ahí el trato ya está cerrado y la ficha hay que crearla —el
     *  trabajo del prospecto solo se traspasa al ganarlo—, así que bloquear no
     *  ofrece ninguna salida, deja la tarjeta atascada y el trabajo colgando.
     *  Se avisa en su lugar (ver `checkConversionAddress` y el toast de
     *  `convertLeadToClient`). Teléfono, correo y nombre siguen bloqueando. */
    permitirDireccionRepetida?: boolean;
}

/** Las dos agendas en una sola lista. Lee la caché de React Query primero y
 *  solo sale a la red cuando falta —el caso de una pestaña recién abierta que
 *  guarda antes de que la vista correspondiente se haya montado. */
async function loadContactRecords(
    qc: QueryClient,
    wsId: string,
    opts: DedupOptions,
): Promise<ContactRecord[]> {
    let clients = qc.getQueryData<Client[]>(CRM_KEYS.clients(wsId));
    if (!clients) {
        const { data } = await supabase
            .from('clients')
            .select('id,name,phone,email,address')
            .eq('workspace_id', wsId);
        clients = (data || []) as Client[];
    }
    const records: ContactRecord[] = clients.map(clientContactRecord);

    if (!opts.skipLeads) {
        let leads = qc.getQueryData<Lead[]>(CRM_KEYS.leads(wsId));
        if (!leads) {
            const { data } = await supabase
                .from('leads')
                .select('id,name,phone,email,address,status')
                .eq('workspace_id', wsId);
            leads = (data || []) as Lead[];
        }
        records.push(...leads.filter(isLeadEligibleForDedup).map(leadContactRecord));
    }
    return records;
}

/**
 * Throws if `candidate` collides with a client OR a lead already in the
 * workspace. Enforced here at the mutation layer so EVERY create/edit path is
 * protected, regardless of which UI triggered it — the New Client form, the
 * New Lead modal, the lead detail panel, and the inline "new client" step of
 * the estimate wizard all funnel through these mutations.
 *
 * El error lleva `code = DUPLICATE_CONTACT`: es una validación esperada, no un
 * fallo, y quien lo reciba tiene que poder distinguirlo sin leer el mensaje
 * (que va traducido).
 */
async function assertNoDuplicateContact(
    qc: QueryClient,
    wsId: string,
    t: Traducir,
    candidate: { name?: string; phone?: string; email?: string; address?: string },
    opts: DedupOptions = {},
) {
    const records = await loadContactRecords(qc, wsId, opts);
    const dup = findDuplicateContact(records, candidate, {
        clientId: opts.excludeClientId,
        leadId: opts.excludeLeadId,
    }, {
        permitirNombreRepetido: opts.permitirNombreRepetido,
        permitirDireccionRepetida: opts.permitirDireccionRepetida,
    });
    if (dup) throw duplicateContactError(describeDuplicateContact(t, dup));
}

type IdentityFields = { name?: string; phone?: string; email?: string; address?: string };

/** Campos de identidad que una edición CAMBIA de verdad.
 *
 *  Un formulario reenvía todos sus campos, y la producción ya arrastra filas
 *  duplicadas de antes de que existiera esta regla: sin esto, tocar una nota en
 *  una ficha duplicada chocaría con su gemela y la dejaría imposible de editar.
 *  Solo se comprueba lo que la edición mueve.
 *
 *  Los CUATRO campos viajan SUELTOS: cada uno bloquea por su cuenta, la
 *  dirección y el nombre incluidos. Antes nombre y dirección iban en pareja,
 *  porque la regla los usaba juntos; al pasar cada uno a bloquear solo,
 *  emparejarlos solo conseguía que renombrar a alguien disparara una
 *  comprobación de dirección que no se había tocado.
 *
 *  Que el nombre solo se mire cuando CAMBIA es lo que mantiene editables las
 *  fichas duplicadas que producción ya arrastra: los dos "Jose" que existen hoy
 *  se siguen pudiendo abrir y guardar mientras nadie les toque el nombre. */
function changedIdentityFields(
    updates: IdentityFields,
    current: { name?: string | null; phone?: string | null; email?: string | null; address?: string | null } | undefined,
): IdentityFields {
    const changed: IdentityFields = {};
    if (updates.phone !== undefined && normalizePhone(updates.phone) !== normalizePhone(current?.phone)) changed.phone = updates.phone;
    if (updates.email !== undefined && normalizeEmail(updates.email) !== normalizeEmail(current?.email)) changed.email = updates.email;
    if (updates.address !== undefined && normalizeAddress(updates.address) !== normalizeAddress(current?.address)) changed.address = updates.address;
    if (updates.name !== undefined && normalizeName(updates.name) !== normalizeName(current?.name)) changed.name = updates.name;
    return changed;
}

/** ¿Hay algo que comprobar? Cualquiera de los cuatro, por su cuenta. */
const worthChecking = (c: IdentityFields): boolean =>
    !!c.phone || !!c.email || !!c.address || !!c.name;

export function useAddLead() {
    const qc = useQueryClient();
    const { t } = useIdioma();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async (leadData: Omit<Lead, 'id' | 'dateCreated' | 'status' | 'tags'>) => {
            // Refuse to create against the 'default' fallback (wrong-workspace write).
            const wsId = requireWorkspaceId();
            await assertNoDuplicateContact(qc, wsId, t, {
                name: leadData.name,
                phone: leadData.phone,
                email: leadData.email,
                address: leadData.address,
            });
            const payload = mapLeadToDB({ ...leadData, status: 'New', tags: [] }, wsId);
            const { error } = await supabase.from('leads').insert([payload]);
            if (error) throw error;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: CRM_KEYS.leads(wsId) }),
    });
}

export function useUpdateLead() {
    const qc = useQueryClient();
    const { t } = useIdioma();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<Lead> }) => {
            const current = (qc.getQueryData<Lead[]>(CRM_KEYS.leads(wsId)) || []).find(l => l.id === id);

            /* Ganado no se deshace, y esto es lo último que lo impide.
               Va en la mutación y no solo en la pantalla porque la razón no es de
               interfaz: ganar un prospecto YA creó su ficha de cliente y le
               traspasó las propuestas. Sacarlo de Ganado dejaría un cliente sin
               prospecto y unas propuestas apuntando a un trato inexistente, sin
               ninguna pantalla en la app para arreglarlo. Ver utils/leadStageFlow.ts. */
            if (updates.status !== undefined && current
                && isLeadStageLocked(current.status) && updates.status !== current.status) {
                throw stageLockedError(describeStageBlock(t, 'won-is-final', current.name));
            }

            // La mayoría de las ediciones de un prospecto son de etapa, de nota o
            // de próxima acción: si no se mueve ningún campo de identidad, no se
            // consulta nada.
            const changed = changedIdentityFields(updates, current);
            if (worthChecking(changed)) {
                await assertNoDuplicateContact(qc, wsId, t, changed, { excludeLeadId: id });
            }
            const payload = mapLeadToDB(updates, wsId);
            delete payload.workspace_id;
            if (Object.keys(payload).length > 0) {
                const { error } = await supabase.from('leads').update(payload).eq('id', id).eq('workspace_id', wsId);
                if (error) throw error;
            }
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: CRM_KEYS.leads(wsId) }),
    });
}

/** Alta de cliente. `fromLeadId` marca la conversión de un prospecto y
 *  `porFirma` que esa conversión la disparó una propuesta firmada desde el
 *  enlace público. Ninguno de los dos viaja a la base (`mapClientToDB` solo
 *  copia columnas conocidas): el primero desactiva la comparación contra la
 *  agenda de prospectos y levanta el bloqueo por dirección repetida, el segundo
 *  levanta el bloqueo por nombre repetido. */
export type NewClientInput = Omit<Client, 'id' | 'dateCreated' | 'totalSpent'>
    & { fromLeadId?: string; porFirma?: boolean };

/**
 * Quita prospectos del tablero. Uno o muchos: la limpieza de la columna Ganado
 * manda una lista, y el panel de detalle manda una lista de uno.
 *
 * Solo se borra lo que se puede borrar sin perder nada —hoy, un GANADO, cuya
 * ficha de cliente conserva contacto, propuestas y proyecto—. La comprobación
 * vive aquí y no solo en el botón porque un borrado no se deshace: si la caché
 * dice que uno de los ids es un prospecto activo, no se borra NINGUNO. Un
 * borrado a medias de una lista es peor que no haber borrado.
 *
 * Cuando la caché no conoce el id no se puede comprobar, y se deja pasar. Es el
 * mismo criterio que el resto de puertas de este archivo: la caché está
 * caliente siempre que hay una pantalla enseñando lo que se va a borrar.
 */
export function useDeleteLeads() {
    const qc = useQueryClient();
    const { t } = useIdioma();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async (ids: string[]) => {
            if (ids.length === 0) return;
            const wsId = requireWorkspaceId();

            const conocidos = qc.getQueryData<Lead[]>(CRM_KEYS.leads(wsId)) || [];
            const protegido = conocidos.find(
                l => ids.includes(l.id) && !isLeadRemovableFromBoard(l.status),
            );
            if (protegido) throw new Error(t('prospectos.borrar.soloGanados', { nombre: protegido.name }));

            const { error } = await supabase.from('leads').delete().in('id', ids).eq('workspace_id', wsId);
            if (error) throw error;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: CRM_KEYS.leads(wsId) }),
    });
}

export function useAddClient() {
    const qc = useQueryClient();
    const { t } = useIdioma();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async (clientData: NewClientInput) => {
            // Refuse to create against the 'default' fallback (wrong-workspace write).
            const wsId = requireWorkspaceId();
            await assertNoDuplicateContact(qc, wsId, t, clientData, {
                skipLeads: !!clientData.fromLeadId,
                permitirNombreRepetido: !!clientData.porFirma,
                permitirDireccionRepetida: !!clientData.fromLeadId,
            });
            const payload = mapClientToDB({ ...clientData, totalSpent: 0 }, wsId);
            const { data, error } = await supabase.from('clients').insert([payload]).select().single();
            if (error) throw error;
            return data?.id || '';
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: CRM_KEYS.clients(wsId) }),
    });
}

export function useUpdateClient() {
    const qc = useQueryClient();
    const { t } = useIdioma();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<Client> }) => {
            // Only dedup-check identifying fields that ACTUALLY changed. Edits
            // resend the whole form, and production already has duplicate rows;
            // without this, editing notes on a pre-existing dup would wrongly
            // block on its twin and make the record un-editable.
            const current = (qc.getQueryData<Client[]>(CRM_KEYS.clients(wsId)) || []).find(c => c.id === id);
            const changed = changedIdentityFields(updates, current);
            if (worthChecking(changed)) {
                await assertNoDuplicateContact(qc, wsId, t, changed, { excludeClientId: id });
            }
            const payload = mapClientToDB(updates, wsId);
            delete payload.workspace_id;
            if (Object.keys(payload).length > 0) {
                const { error } = await supabase.from('clients').update(payload).eq('id', id).eq('workspace_id', wsId);
                if (error) throw error;
            }
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: CRM_KEYS.clients(wsId) }),
    });
}

export function useDeleteClient() {
    const qc = useQueryClient();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async (id: string) => {
            // Atomic path: the RPC deletes appointments + client in ONE transaction,
            // closing the partial-failure window (appointments gone but client left)
            // of the two-step client-side cascade below.
            const { error: rpcErr } = await supabase.rpc('delete_client_cascade', { p_client_id: id });
            if (!rpcErr) return;
            // Fall back only when the function itself is absent (migration not yet
            // applied): Postgres undefined_function 42883, or PostgREST's
            // schema-cache miss PGRST202. Any other RPC error is a real failure —
            // rethrow it rather than silently re-running the non-atomic path.
            const notDeployed = rpcErr.code === '42883' || rpcErr.code === 'PGRST202';
            if (!notDeployed) throw rpcErr;

            // Transitional fallback (pre-migration): appointments.client_id is NOT
            // NULL while its FK is ON DELETE SET NULL, so deleting a client with
            // appointments would fail with 23502. Delete their appointments first
            // (workspace-scoped to respect tenancy). Calls need no handling —
            // calls.client_id is nullable, so its SET NULL works.
            const { error: apptErr } = await supabase.from('appointments').delete().eq('client_id', id).eq('workspace_id', wsId);
            if (apptErr) throw apptErr;
            const { error } = await supabase.from('clients').delete().eq('id', id).eq('workspace_id', wsId);
            if (error) throw error;
        },
        // The delete cascades to appointments (RPC, or the fallback above), so
        // both caches go stale — invalidate each on its workspace-scoped key.
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: CRM_KEYS.clients(wsId) });
            qc.invalidateQueries({ queryKey: CRM_KEYS.appointments(wsId) });
        },
    });
}

export function useAddAppointment() {
    const qc = useQueryClient();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async (apptData: Omit<Appointment, 'id'>) => {
            // Refuse to create against the 'default' fallback (wrong-workspace write).
            const wsId = requireWorkspaceId();
            const payload = mapAppointmentToDB(apptData, wsId);
            const { error } = await supabase.from('appointments').insert([payload]);
            if (error) throw error;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: CRM_KEYS.appointments(wsId) }),
    });
}

export function useUpdateAppointment() {
    const qc = useQueryClient();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<Appointment> }) => {
            const payload = mapAppointmentToDB(updates, wsId);
            delete payload.workspace_id;
            if (Object.keys(payload).length > 0) {
                const { error } = await supabase.from('appointments').update(payload).eq('id', id).eq('workspace_id', wsId);
                if (error) throw error;
            }
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: CRM_KEYS.appointments(wsId) }),
    });
}

export function useDeleteAppointment() {
    const qc = useQueryClient();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async (id: string) => {
            const { error } = await supabase.from('appointments').delete().eq('id', id).eq('workspace_id', wsId);
            if (error) throw error;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: CRM_KEYS.appointments(wsId) }),
    });
}
